import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
export type ObjectAccessRole = 'Owner' | 'Editor' | 'Viewer';
export type ShareableRole = Exclude<ObjectAccessRole, 'Owner'>;

export type ShareListEntry = {
  email: string;
  full_name: string;
  role: ObjectAccessRole;
  actor_id: string;
};

export type ShareListResponse = { shares: ShareListEntry[] };

type Props = {
  open: boolean;
  title: string;
  currentUserEmail?: string;
  canManage: boolean;
  listShares: () => Promise<ShareListResponse>;
  share: (email: string, role: ShareableRole) => Promise<unknown>;
  revoke: (email: string) => Promise<unknown>;
  onClose: () => void;
};

function initialFromNameOrEmail(nameOrEmail: string): string {
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
}

function isValidEmail(value: string): boolean {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function ShareAccessDialog({
  open,
  title,
  currentUserEmail,
  canManage,
  listShares,
  share,
  revoke,
  onClose,
}: Props) {
  const { t } = useTranslation('sharing');
  const [shares, setShares] = React.useState<ShareListEntry[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [emailInput, setEmailInput] = React.useState('');
  const [newRole, setNewRole] = React.useState<ShareableRole>('Viewer');
  const [submitting, setSubmitting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listShares();
      setShares(res.shares ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('accessDialog.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [listShares, t]);

  React.useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  const handleAdd = async () => {
    const email = emailInput.trim();
    if (!isValidEmail(email)) {
      setError(t('accessDialog.errors.invalidEmail'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await share(email, newRole);
      setEmailInput('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('accessDialog.errors.shareFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRole = async (email: string, role: ShareableRole) => {
    setSubmitting(true);
    setError(null);
    try {
      await share(email, role);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('accessDialog.errors.updateRoleFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (email: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await revoke(email);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('accessDialog.errors.revokeFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const normalizedCurrentEmail = (currentUserEmail ?? '').trim().toLowerCase();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="share-access-dialog-title" sx={{ zIndex: 2500 }}>
      <DialogTitle id="share-access-dialog-title" sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label={t('accessDialog.closeAriaLabel')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Icon size={20} name="xmark" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={t('accessDialog.addPeoplePlaceholder')}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
            disabled={!canManage || submitting}
          />
          <FormControl sx={{ minWidth: 140 }} size="small">
            <Select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as ShareableRole)}
              disabled={!canManage || submitting}
              aria-label={t('accessDialog.roleAriaLabel')}
              MenuProps={{ sx: { zIndex: 2600 } }}
            >
              <MenuItem value="Viewer">{t('accessDialog.roles.viewer')}</MenuItem>
              <MenuItem value="Editor">{t('accessDialog.roles.editor')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => void handleAdd()}
            disabled={!canManage || submitting || !isValidEmail(emailInput)}
          >
            {t('accessDialog.add')}
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('accessDialog.peopleWithAccess')}
          </Typography>
          <Button variant="text" size="small" onClick={() => void refresh()} disabled={loading || submitting}>
            {t('accessDialog.refresh')}
          </Button>
        </Box>

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            {t('accessDialog.loading')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {shares.map((entry) => {
              const displayName = entry.full_name?.trim() || entry.email;
              const isOwnerRow = entry.role === 'Owner';
              const isCurrentUser = normalizedCurrentEmail && entry.email.trim().toLowerCase() === normalizedCurrentEmail;
              const canEditRowRole = canManage && !isOwnerRow;
              const canRemoveRow = (canManage && !isOwnerRow) || (!canManage && isCurrentUser && !isOwnerRow);

              return (
                <ListItem
                  key={entry.actor_id || entry.email}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={entry.role}
                          onChange={(e) => void handleChangeRole(entry.email, e.target.value as ShareableRole)}
                          disabled={!canEditRowRole || submitting}
                          aria-label={t('accessDialog.roleForEmailAriaLabel', { email: entry.email })}
                          MenuProps={{ sx: { zIndex: 2600 } }}
                        >
                          <MenuItem value="Owner" disabled>
                            {t('accessDialog.roles.owner')}
                          </MenuItem>
                          <MenuItem value="Viewer">{t('accessDialog.roles.viewer')}</MenuItem>
                          <MenuItem value="Editor">{t('accessDialog.roles.editor')}</MenuItem>
                        </Select>
                      </FormControl>
                      {canRemoveRow && (
                        <Tooltip title={canManage ? t('accessDialog.removeAccess') : t('accessDialog.leave')}>
                          <IconButton
                            edge="end"
                            onClick={() => void handleRevoke(entry.email)}
                            disabled={submitting}
                            aria-label={canManage
                              ? t('accessDialog.removeForEmailAriaLabel', { email: entry.email })
                              : t('accessDialog.leaveForEmailAriaLabel', { email: entry.email })}
                          >
                            <Icon name="user" size={20} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  sx={{ px: 0 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {initialFromNameOrEmail(displayName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={displayName}
                    secondary={entry.email}
                    primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                  />
                </ListItem>
              );
            })}
            {shares.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                {t('accessDialog.emptyAccessList')}
              </Typography>
            )}
          </List>
        )}

        {!canManage && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">{t('accessDialog.viewOnlyNotice')}</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('accessDialog.done')}</Button>
      </DialogActions>
    </Dialog>
  );
}
